<template>
	<ui-dialog
		ref="dialog"
		:closable="!isProcessing"
		class="media-editor-dialog"
		large
		@open="setup"
	>
		<template slot="header">
			<h6 class="ui-title">Trim {{ mediaItem.comment || mediaItem.title }}</h6>
		</template>

		<WebPlayer
			v-show="!isProcessing"
			ref="webPlayer"
			:on-player-event="onPlayerEvent"
		/>

		<div v-if="isProcessing" class="processing-video-overlay">
			<div class="processing-video-overlay__spinner">
				<div class="ui-progress-dots">
					<div class="bounce1"></div>
					<div class="bounce2"></div>
					<div class="bounce3"></div>
				</div>
			</div>
			<div class="processing-video-overlay__text">
				<h2 class="ui-title">Processing media</h2>
			</div>
		</div>

		<div class="editor">
			<editor-timeline
				ref="editorTimeline"
				:duration="duration"
				:mark-in-pos.sync="markInPos"
				:mark-out-pos.sync="markOutPos"
				:player-position="position"
				@goToPosition="goToPosition($event)"
			/>
			<editor-controls
				ref="editorControls"
				:is-playing="isPlaying"
				@markIn="markIn()"
				@markOut="markOut()"
				@goToIn="goToIn()"
				@stepBackward="stepBackward()"
				@pause="pause()"
				@play="play()"
				@stepForward="stepForward()"
				@goToOut="goToOut()"
			/>
			<ui-dialog-footer>
				<ui-checkbox
					v-model="deleteOriginal"
					label="Delete original video"
					tooltip="Checking this will delete the original video"
				/>
				<ui-confirm-button
					confirm-text="Confirm"
					secondary
					text="Apply"
					tooltip="Create a new video out of the selected range"
					@click="trim"
				/>
			</ui-dialog-footer>
			<div v-if="isProcessing" class="blocker-overlay"></div>
		</div>
	</ui-dialog>
</template>

<script>
import alert from "lib/modules/Alert";
import http from "lib/modules/Http";

import EditorControls from "components/media/EditorControls";
import EditorTimeline from "components/media/EditorTimeline";
import UiCheckbox from "lib/components/ui/Checkbox";
import UiConfirmButton from "lib/components/ui/ConfirmButton";
import UiDialog from "lib/components/ui/Dialog";
import UiDialogFooter from "lib/components/ui/DialogFooter";
import WebPlayer from "lib/components/WebPlayer";

export default {
	name: "MediaEditorDialog",
	components: {
		EditorControls,
		EditorTimeline,
		UiCheckbox,
		UiConfirmButton,
		UiDialog,
		UiDialogFooter,
		WebPlayer
	},
	data() {
		return {
			deleteOriginal: false,
			duration: 0,
			isPlaying: false,
			isProcessing: false,
			markInPos: -1,
			markOutPos: -1,
			mediaItem: {},
			position: 0
		};
	},
	created() {
		this.seekPosition = null;
	},
	mounted() {
		this.trimMedia = (mediaItem, recorderApiUrl, pos = 0) => {
			this.duration = mediaItem.duration;
			this.deleteOriginal = false;
			this.mediaItem = mediaItem;
			this.position = pos;
			this.recorderApiUrl = recorderApiUrl;
			this.$refs.dialog.open();
		};
	},
	methods: {
		trim() {
			const trimRequest = {
				start: this.markInPos,
				duration: this.markOutPos - this.markInPos
			};

			this.pause();
			this.isProcessing = true;

			http.post(`${this.recorderApiUrl}/media/${this.mediaItem.media_id}/cut`, trimRequest)
			.then(() => {
				this.$refs.dialog.close();

				if (this.deleteOriginal)
					http.delete(`${this.recorderApiUrl}/media/${this.mediaItem.media_id}`)
					.catch((error) => {
						alert.error("Unable to delete media", error.response);
					});
			})
			.catch((error) => {
				alert.error("Unable to edit media", error.response);
			})
			.finally(() => {
				this.isProcessing = false;
			});
		},
		goToIn() {
			this.goToPosition(this.markInPos);
		},
		goToOut() {
			this.goToPosition(this.markOutPos);
		},
		goToPosition(pos) {
			this.$refs.webPlayer.player.seek(pos);
		},
		markIn() {
			this.$refs.editorTimeline.markIn();
		},
		markOut() {
			this.$refs.editorTimeline.markOut();
		},
		onPlayerEvent(evt) {
			switch (evt.type) {
				case "buffering":
				case "play":
					this.isPlaying = true;
					break;

				case "idle":
				case "pause":
				case "error":
					this.isPlaying = false;
					break;

				case "seek":
					this.seekPosition = evt.offset;
					break;

				case "seeked":
					this.pause();
					this.position = this.seekPosition; // update position prop since "seek" does not trigger time event
					break;

				case "time":
					this.position = evt.position;
					break;

				case "ready":
					if (this.position === 0)
						this.goToPosition(this.markInPos);
					else
						this.goToPosition(this.position);

					break;

				default:
					break;
			}
		},
		pause() {
			this.$refs.webPlayer.player.pause();
		},
		play() {
			this.$refs.webPlayer.player.play();
		},
		setup() {
			this.$refs.webPlayer.setup({
				autostart: false,
				file: `${this.recorderApiUrl}/media?secid=${encodeURIComponent(this.mediaItem.secid)}&type=video`,
				type: "mp4"
			});
		},
		stepBackward() {
			this.goToPosition(this.$refs.webPlayer.player.getPosition() - 1 - 1 / 30); // subtract 1 sec - 1 frame
		},
		stepForward() {
			this.goToPosition(this.$refs.webPlayer.player.getPosition() + 1 + 1 / 30); // add 1 sec + 1 frame
		}
	}
};
</script>

<style lang="scss" scoped>
.ui-title {
	margin-top: 0;
}

.ui-checkbox {
	margin-top: 0;
	margin-bottom: 0;
}

.processing-video-overlay {
	background-color: #000;
	padding-top: 56.25%; /* 16:9 Aspect Ratio */
	position: relative;
	width: 100%;
	.processing-video-overlay__spinner {
		position: absolute;
		top: 0;
		left: 0;
		height: 100%;
		width: 100%;
	}
	.processing-video-overlay__text {
		position: absolute;
		top: 50%;
		left: 50%;
		-ms-transform: translate(-50%, -50%);
		-webkit-transform: translate(-50%, -50%);
		transform: translate(-50%, -50%);
		min-width: 346px;
		text-align: center;
		color: #aaa;
		margin-top: -60px;
	}
}

.editor {
	position: relative;
	.blocker-overlay {
		position: absolute;
		top: 0;
		right: 0;
		bottom: 0;
		left: 0;
		background-color: rgba(0, 0, 0, 0.6);
		z-index: 5;
	}
}
</style>
